import { css } from '@emotion/react'
import {
  Button,
  Heading,
  HFlow,
  Icon,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalProps,
  Switch,
  Tag,
  Text,
  Theme,
  Tooltip,
  useTheme,
  VFlow,
} from 'bold-ui'
import { Box } from 'components/Box'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

interface CookiePreferencesModalMobileProps extends Omit<ModalProps, 'open'> {
  onClickAceitarNecessarios: () => void
  onClickAceitarTodos: () => void
  onOpenTermosUso: () => void
}

export const CookiePreferencesModalMobile = (props: CookiePreferencesModalMobileProps) => {
  const { onClickAceitarNecessarios, onClickAceitarTodos, onOpenTermosUso, ...rest } = props
  const theme = useTheme()
  const [enableCookiesDesempenho, setEnableCookiesDesempenho] = useState(false)
  const styles = useMemo(() => createStyles(theme, enableCookiesDesempenho), [theme, enableCookiesDesempenho])

  const refModal = useRef<HTMLDivElement>()
  const toggleEnableCookiesDesempenho = useCallback(() => {
    setEnableCookiesDesempenho((prev) => !prev)
  }, [])

  // TODO@RNG - Adaptação de modal sera generalizada na issue #19068
  useEffect(() => {
    const containerElement = refModal.current?.parentNode as Element

    if (containerElement) {
      const estiloCss = css(styles.parentModal)
      containerElement.setAttribute('style', estiloCss.styles)
    }
  }, [styles.parentModal])

  return (
    <Modal containerRef={refModal} size='large' open style={styles.modal} {...rest}>
      <ModalHeader title='Configurações avançadas de cookies' icon={{ name: 'cookieOutline', fill: 'primary' }} />
      <ModalBody style={styles.modalBody}>
        <VFlow style={styles.cookieConfigContainer} vSpacing={2}>
          <VFlow vSpacing={2}>
            <Box>
              <VFlow style={styles.declaracaoContainer}>
                <HFlow hSpacing={0.5}>
                  <Text>
                    Para melhorar a sua experiência no sistema e prover serviços personalizados, utilizamos cookies.
                  </Text>
                </HFlow>
                <Button
                  kind='normal'
                  skin='default'
                  size='small'
                  style={styles.declaracaoButton}
                  onClick={onOpenTermosUso}
                >
                  <Icon icon='fileVisualizationOutline' />
                  Ver declaração de cookies
                </Button>
              </VFlow>
            </Box>
            <VFlow vSpacing={1}>
              <Box>
                <VFlow vSpacing={0.5}>
                  <HFlow alignItems='center' justifyContent='space-between'>
                    <Heading level={3}>Cookies estritamente necessários</Heading>
                  </HFlow>
                  <Text>
                    Esses cookies permitem funcionalidades essenciais, tais como a manutenção e segurança da sessão do
                    usuário. Esses cookies não podem ser desativados em nossos sistemas. Embora sejam necessários, você
                    pode bloquear esses cookies diretamente no seu navegador, mas isso pode comprometer sua experiência
                    e prejudicar o funcionamento do site.
                  </Text>
                  <HFlow style={styles.cookiesEssenciaisSwitchState}>
                    Ativado
                    <Tooltip text='Cookies obrigatórios não podem ser desativados'>
                      <Tag style={styles.switchLocked}>
                        <Icon icon='lockCloseFilled' style={styles.lock} size={1} />
                      </Tag>
                    </Tooltip>
                  </HFlow>
                </VFlow>
              </Box>
              <Box>
                <VFlow vSpacing={0.5}>
                  <HFlow alignItems='center' justifyContent='space-between'>
                    <Heading level={3}>Cookies de desempenho</Heading>
                  </HFlow>
                  <Text>
                    Cookies de desempenho visam a melhoria do desempenho do site por meio da coleta de dados
                    anonimizados sobre navegação e uso dos recursos disponibilizados. Se você não permitir a coleta
                    desses cookies, esses dados não serão usados para melhoria do site.
                  </Text>
                  <HFlow style={styles.cookiesDesempenhoSwitchState}>
                    {enableCookiesDesempenho ? 'Ativado' : 'Desativado'}
                    <Switch onChange={toggleEnableCookiesDesempenho} />
                  </HFlow>
                </VFlow>
              </Box>
            </VFlow>
          </VFlow>
        </VFlow>
      </ModalBody>
      <ModalFooter>
        <VFlow>
          <Button onClick={onClickAceitarTodos} style={styles.submitButton}>
            Aceitar todos
          </Button>
          <Button
            onClick={enableCookiesDesempenho ? onClickAceitarTodos : onClickAceitarNecessarios}
            kind='primary'
            style={styles.submitButton}
          >
            Salvar configuração
          </Button>
        </VFlow>
      </ModalFooter>
    </Modal>
  )
}

const createStyles = (theme: Theme, cookiesDesempenhoState: boolean) => ({
  declaracaoButton: css`
    width: 14rem;
    height: 2rem;
    color: ${theme.pallete.primary.c40};
    border-color: ${theme.pallete.primary.c40};
  `,
  submitButton: css`
    width: 100%;
  `,
  cookieConfigContainer: css`
    width: 100%;
  `,
  declaracaoContainer: css`
    align-items: flex-end;
  `,
  cookiesEssenciaisSwitchState: css`
    font-weight: bold;
    align-items: center;
    justify-content: end;
    font-size: 1rem;
    color: ${theme.pallete.primary.c40};
  `,
  cookiesDesempenhoSwitchState: css`
    font-weight: bold;
    align-items: center;
    justify-content: end;
    font-size: 1rem;
    color: ${cookiesDesempenhoState ? theme.pallete.primary.c40 : theme.pallete.gray.c40};
  `,
  switchLocked: css`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 3rem;
    height: 1.5rem;
    background-color: ${theme.pallete.primary.c80};
    border-radius: 0.75rem;
  `,
  lock: css`
    fill: ${theme.pallete.gray.c100};
  `,
  parentModal: css`
    padding: 0;
    width: 100%;
    height: 100%;
  `,
  modal: css`
    height: 100%;
    max-height: 100vh;
    min-width: min-content;
    width: 100%;
  `,
  modalBody: css`
    padding: 1rem;
  `,
})
